import React, { useState } from 'react';
import './DomainStep.scss';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useProjectWizard } from '../../contexts/ProjectWizardContext';
import AnalyzeLoader, { ANALYZE_LOADER_TOTAL_MS } from '../analyze-loader/AnalyzeLoader';
import { DOMAIN_REGEX } from '../../../../../shared/utils/regex';
import { getQuestions } from '../../../../../shared/services/projects/ProjectService';
import { cleanDomain, getSuggestedDomain } from '../../../../../shared/utils/utils';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import WizardSuggestionTag from '../suggestion-tag/WizardSuggestionTag';

const DomainStep: React.FC = () => {
  const { setActiveStep, setQuestionResponse, setDomain } = useProjectWizard();
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const [apiError, setApiError] = useState<string | null>(null);
  const suggestedDomain = getSuggestedDomain(accountResponse?.email, accountResponse?.language);

  const onSubmit = async (loginFormData: any) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    const domain = cleanDomain(loginFormData.domain).trim();
    const minDisplay = new Promise((resolve) => setTimeout(resolve, ANALYZE_LOADER_TOTAL_MS));
    Promise.all([getQuestions(domain, true), minDisplay])
      .then(([data]) => {
        setQuestionResponse(data);
        setActiveStep('QUESTIONS');
        setDomain(domain);
      })
      .catch((err) => {
        setApiError(err.message);
      })
      .finally(() => {
        setLoading(false);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      {loading && <AnalyzeLoader />}
      <form onSubmit={handleSubmit(onSubmit)} className="project-wizard__page-box">
        <div className="project-wizard__form">
          <div className="form-box__row">
            <h2>{t('enterDomain')}</h2>
            <div
              className="project-wizard__desc"
              dangerouslySetInnerHTML={{ __html: t('enterDomainDesc') }}
            />
            <div className="form-box__label">{t('domain')}:</div>
            <div className="wizard-suggestion-field">
              <InputText
                {...register('domain', {
                  required: t('fieldIsRequired'),
                  pattern: {
                    value: DOMAIN_REGEX,
                    message: t('wrongDomain'),
                  },
                })}
                placeholder="example.com"
                className={`input--xxl ${errors.domain || apiError ? 'form-box__invalid' : ''}`}
              />
              {errors.domain && <span>{errors.domain.message as string}</span>}
            </div>
            <WizardSuggestionTag
              value={suggestedDomain}
              onApply={(value) => setValue('domain', value, { shouldValidate: true })}
            />
          </div>
        </div>
        <div className="project-wizard__action">
          <div>
            <Button className="btn btn--xxl" type="submit" disabled={isSubmitting}>
              {t('next')}
            </Button>
          </div>
          <div className="project-wizard__link">
            {t('linkToBrandNameStep')}
            <Button
              className="btn--link btn--link-black"
              label={t('clickHere')}
              onClick={() => setActiveStep('FIRM_NAME')}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default DomainStep;
